import {
  FetchCacheItemsSources,
  FetchCacheItemsTrackTotalHits,
  FetchItemCacheKey,
  FetchItemsGqlQuery
} from '../../../../types';
import {
  FetchCustomFieldsCacheKey,
  FetchCacheCustomFieldsFilters,
  FetchCustomFieldsSort,
  FetchCustomFieldsPage,
  FetchCustomFieldsLimit,
  FetchCustomFieldsGqlQuery
} from '../../customFieldsTypes';
import { FetchCacheCustomFieldsQueryResponse } from '../../queries/fetchCacheCustomFields.query';

import { useCacheIndexQuery } from '../../../common/reactQuery/hooks/useCacheIndexQuery';

import {
  INITIAL_CUSTOM_FIELD_FILTERS,
  INITIAL_CUSTOM_FIELD_LIMIT,
  INITIAL_CUSTOM_FIELD_PAGE,
  INITIAL_CUSTOM_FIELD_SORT
} from '../../customFieldsConstants';

interface CustomFieldsWithPrefetchItemOptions {
  fetchItemCacheKey: FetchItemCacheKey;
  fetchItemQuery: FetchItemsGqlQuery;
}

interface CustomFieldsWithoutPrefetchItemOptions {
  fetchItemCacheKey?: never;
  fetchItemQuery?: never;
}

interface CustomFieldsDefaultOptions {
  action?: string;
  cacheKey: FetchCustomFieldsCacheKey;
  query: FetchCustomFieldsGqlQuery;
  initialFilters?: FetchCacheCustomFieldsFilters;
  initialSort?: FetchCustomFieldsSort;
  initialPage?: FetchCustomFieldsPage;
  initialLimit?: FetchCustomFieldsLimit;
  source?: FetchCacheItemsSources;
  trackTotalHits?: FetchCacheItemsTrackTotalHits;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    keepPreviousData?: boolean;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
    withoutPrefetch?: boolean;
  };
}

type CustomFieldsOptions = CustomFieldsDefaultOptions &
  (
    | CustomFieldsWithPrefetchItemOptions
    | CustomFieldsWithoutPrefetchItemOptions
  );

const scope = 'customFields';

function useCachePaginatedCustomFields<
  FetchCustomFieldsType = FetchCacheCustomFieldsQueryResponse
>({
  action,
  cacheKey,
  initialFilters = INITIAL_CUSTOM_FIELD_FILTERS,
  initialSort = INITIAL_CUSTOM_FIELD_SORT,
  initialPage = INITIAL_CUSTOM_FIELD_PAGE,
  initialLimit = INITIAL_CUSTOM_FIELD_LIMIT,
  fetchItemCacheKey,
  fetchItemQuery,
  query,
  source = FetchCacheItemsSources.DB,
  trackTotalHits,
  options = {}
}: CustomFieldsOptions) {
  const {
    data,
    items,
    itemsError,
    itemsErrorMessage,
    itemsTotalCount,
    isFetched,
    isLoading,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    updateItemCache,
    updateItemsCache,
    clearItemsFilters,
    filterItems,
    changeItemsFilters,
    prefetchItems,
    sortItems,
    paginateItems,
    limitItems,
    prefetchItem
  } = useCacheIndexQuery<FetchCustomFieldsType>({
    action,
    cacheKey,
    cacheQuery: query,
    defaultApi: 'cache_api',
    scope,
    query,
    initialFilters,
    initialLimit,
    initialPage,
    initialSort,
    fetchItemCacheKey,
    fetchItemQuery,
    options,
    source,
    trackTotalHits
  });

  return {
    customFieldsData: data,
    customFields: items,
    customFieldsError: itemsError,
    customFieldsErrorMessage: itemsErrorMessage,
    customFieldsTotalCount: itemsTotalCount,
    customFieldsFetched: isFetched,
    customFieldsLoading: isLoading,
    customFieldsIsPlaceholderData: isPlaceholderData,
    customFieldsFilters: currentFilters,
    customFieldsSort: currentSort,
    customFieldsPage: currentPage,
    customFieldsLimit: currentLimit,
    updateCustomFieldCache: updateItemCache,
    updateCustomFieldsCache: updateItemsCache,
    filterCustomFields: filterItems,
    changeCustomFieldsFilters: changeItemsFilters,
    clearCustomFieldsFilters: clearItemsFilters,
    sortCustomFields: sortItems,
    paginateCustomFields: paginateItems,
    limitCustomFields: limitItems,
    prefetchCustomFields: prefetchItems,
    prefetchCustomField: prefetchItem
  };
}

export default useCachePaginatedCustomFields;
